import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Download, RefreshCw } from "lucide-react";
import { getFamilias } from "../services/familiasService";
import { getPlanes } from "../services/planesService";
import { getUrls } from "../services/urlsService";
// Remove the unused import
// import { createTabItems } from "./Settings";

export default function Exportar() {
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState("");
  const [dataReady, setDataReady] = useState(false);
  const [urls, setUrls] = useState([]);
  
  // Similar to how FamiliasList and PlanesList load data on component mount
  useEffect(() => {
    // Don't auto-fetch on mount to give user control
    // fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    setDataReady(false);
    setError(null);
    
    try {
      // Stage 1: Fetching familias
      setStage("Obteniendo familias...");
      const familiasResponse = await getFamilias();
      
      // Stage 2: Fetching planes for each familia
      setStage("Obteniendo planes...");
      const planesResponse = await getPlanes();
      
      // Stage 3: Fetching URLs
      setStage("Obteniendo URLs...");
      const urlsResponse = await getUrls();
      
      // Stage 4: Combining data
      setStage("Construyendo JSON...");
      
      // Check if we have valid responses - handle both direct array and nested response formats
      let familiasData = [];
      if (Array.isArray(familiasResponse)) {
        // If response is directly an array (as seen in console)
        familiasData = familiasResponse;
      } else if (familiasResponse && familiasResponse.response && Array.isArray(familiasResponse.response.data)) {
        // If response has the nested structure
        familiasData = familiasResponse.response.data;
      } else {
        console.error("Invalid familias response:", familiasResponse);
        throw new Error("Formato de respuesta de familias inválido");
      }
      
      let planesData = [];
      if (Array.isArray(planesResponse)) {
        planesData = planesResponse;
      } else if (planesResponse && planesResponse.response && Array.isArray(planesResponse.response.data)) {
        planesData = planesResponse.response.data;
      } else {
        console.error("Invalid planes response:", planesResponse);
        throw new Error("Formato de respuesta de planes inválido");
      }
      
      let urlsData = [];
      if (Array.isArray(urlsResponse)) {
        urlsData = urlsResponse;
      } else if (urlsResponse && urlsResponse.response && Array.isArray(urlsResponse.response.data)) {
        urlsData = urlsResponse.response.data;
      } else {
        console.error("Invalid URLs response:", urlsResponse);
        throw new Error("Formato de respuesta de URLs inválido");
      }
      
      // Process familias data - handle both direct objects and fieldData objects
      const processedFamilias = familiasData.map(item => {
        // Check if the item has fieldData or direct properties
        const data = item.fieldData || item;
        return {
          id: data.id,
          uuid: data.uuid,
          titulo: data.titulo,
          descripcion: data.descripcion,
          urlImagen: data.urlImagen,
          colorDestacado: data.colorDestacado,
          status: data.status,
          planes: []
        };
      });
      
      // Process planes data and associate with familias
      planesData.forEach(planeItem => {
        const data = planeItem.fieldData || planeItem;
        const familiaUuid = data.fkFam;
        const familia = processedFamilias.find(f => f.uuid === familiaUuid);
        
        if (familia) {
          // Process portal data items into modalContent format
          const modalContent = {};
          
          // Check if we have groupedItems in the response
          const groupedItems = planeItem.groupedItems || [];
          
          if (groupedItems.length > 0) {
            console.log(`Processing groupedItems for plan: ${data.nombre}`, groupedItems);
            
            // Convert groupedItems to modalContent format
            groupedItems.forEach(group => {
              const tipo = group.tipo || '';
              const titulo = group.titulo || '';
              
              if (!tipo) return;
              
              if (!modalContent[tipo]) {
                modalContent[tipo] = {};
              }
              
              // Extract items from the group
              const items = group.items.map(item => item.item).filter(Boolean);
              
              if (items.length > 0) {
                modalContent[tipo][titulo] = items;
              }
            });
          } else {
            console.log(`No groupedItems found for plan: ${data.nombre}`);
          }
          
          // Add the plan to the familia
          familia.planes.push({
            id: data.recordId, // Use recordId instead of id
            recordId: data.recordId, // Include the original recordId from FileMaker
            uuid: data.uuid,
            nombre: data.nombre,
            descripcion: data.descripcion,
            urlImagen: data.urlImagen,
            badgeDestacador: data.badgeDestacador,
            status: data.status,
            urlContratacion: data.urlContratacion,
            landbotConfig: data.landbotConfig,
            modalContent: modalContent,
            nota: data.nota || ""
          });
        }
      });
      
      // Process URLs data
      const processedUrls = urlsData.map(item => {
        const data = item.fieldData || item;
        return {
          id: data.id,
          uuid: data.uuid,
          name: data.name,
          url: data.url,
          type: data.type,
          customDatacodproducto: data.customDatacodproducto,
          recordId: item.recordId || data.recordId
        };
      });
      
      setFamilias(processedFamilias);
      setUrls(processedUrls);
      setDataReady(true);
      setStage("Datos listos para exportar");
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(`Error al cargar los datos: ${err.message}`);
      setStage("");
    } finally {
      setLoading(false);
    }
  };

  const generateLandbotJson = () => {
    // Extract landbot configurations from all active plans and match with URLs
    const landbotConfigs = {};
    
    familias
      .filter(familia => familia.status === 'active')
      .forEach(familia => {
        familia.planes
          .filter(plan => plan.status === 'active' && plan.landbotConfig)
          .forEach(plan => {
            // Only add if not already in the config
            if (!landbotConfigs[plan.landbotConfig]) {
              // Find matching URL from the URLs data
              const matchingUrl = urls.find(url => url.name === plan.landbotConfig);
              
              if (matchingUrl) {
                const config = {
                  id: parseInt(plan.id, 10), // Ensure ID is a number with base 10
                  name: plan.landbotConfig,
                  type: matchingUrl.type,
                  url: matchingUrl.url
                };
                
                // Add customData if it exists
                if (matchingUrl.customDatacodproducto) {
                  config.customData = {
                    codproducto: matchingUrl.customDatacodproducto
                  };
                }
                
                landbotConfigs[plan.landbotConfig] = config;
              }
            }
          });
      });
    
    // Create the final landbot data structure
    const exportData = {
      landbotConfig: landbotConfigs
    };
    
    return JSON.stringify(exportData, null, 2);
  };

  const handleExportLandbotJson = () => {
    const jsonData = generateLandbotJson();
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = 'landbot-data.json';
    document.body.appendChild(a);
    a.click();
    
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateDataJson = () => {
    // Filter active families and their active plans
    const activeFamilias = familias
      .filter(familia => familia.status === 'active')
      .map(familia => {
        // Get only active plans that belong to this family
        const activePlanes = familia.planes
          .filter(plan => plan.status === 'active')
          .map(plan => {
            // Clean up modalContent by removing empty sections
            const cleanModalContent = {};
            
            // Only include non-empty sections of modalContent
            Object.entries(plan.modalContent).forEach(([key, value]) => {
              if (Object.keys(value).length > 0) {
                cleanModalContent[key] = value;
              }
            });
            
            return {
              id: plan.recordId ? parseInt(plan.recordId, 10) : null, // Use recordId instead of id
              recordId: plan.recordId, // Include the original recordId from FileMaker
              uuid: plan.uuid,
              nombre: plan.nombre,
              descripcion: plan.descripcion,
              urlImagen: plan.urlImagen,
              badgeDestacador: plan.badgeDestacador,
              status: plan.status,
              urlContratacion: plan.urlContratacion,
              landbotConfig: plan.landbotConfig,
              // Explicitly include the family relationship
              fkFam: familia.uuid,
              // Use the cleaned up modalContent or an empty object if all sections are empty
              modalContent: Object.keys(cleanModalContent).length > 0 ? cleanModalContent : {},
              nota: plan.nota || ""
            };
          });

        return {
          id: parseInt(familia.id, 10), // Convert to number
          uuid: familia.uuid,
          titulo: familia.titulo,
          descripcion: familia.descripcion,
          urlImagen: familia.urlImagen,
          colorDestacado: familia.colorDestacado,
          status: familia.status,
          planes: activePlanes
        };
      })
      .filter(familia => familia.planes.length > 0); // Only include families with active plans

    // Create the final data structure
    const exportData = {
      config: {
        familias: activeFamilias
      }
    };

    // Convert to JSON string with proper formatting
    return JSON.stringify(exportData, null, 2);
  };

  const handleExportDataJson = () => {
    const jsonData = generateDataJson();
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create a download link and trigger it
    const a = document.createElement('a');
    a.href = url;
    a.download = 'data.json';
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Exportar Datos</h1>
        <p className="text-gray-600">Exporta los datos de la aplicación en diferentes formatos</p>
      </div>

      {/* Data Fetch Section */}
      <Card>
        <CardHeader>
          <CardTitle>Obtener Datos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Primero debes obtener los datos actualizados antes de exportarlos.
            </p>
            
            {stage && (
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-700">{stage}</p>
              </div>
            )}
            
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            
            <Button 
              onClick={fetchData}
              disabled={loading}
              className="flex items-center"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Obteniendo datos..." : "Obtener datos actualizados"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <Tabs defaultValue="data-json" className="w-full">
        <TabsList className="flex mb-4 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger 
            value="data-json" 
            className="rounded-md px-4 py-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-all"
          >
            data.json
          </TabsTrigger>
          <TabsTrigger 
            value="landbot-json" 
            className="rounded-md px-4 py-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-all"
          >
            landbot-data.json
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="data-json">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Download className="mr-2 h-5 w-5" />
                Exportar data.json
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">
                Este archivo contiene todas las familias activas y sus planes activos en formato JSON.
                Se utiliza para la configuración principal de la aplicación.
              </p>
              
              <Button 
                onClick={handleExportDataJson}
                disabled={!dataReady}
                className="flex items-center"
              >
                <Download className="mr-2 h-4 w-4" />
                Exportar data.json
              </Button>
              
              {!dataReady && (
                <p className="text-xs text-amber-600 mt-2">
                  Primero debes obtener los datos actualizados usando el botón "Obtener datos actualizados"
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="landbot-json">
          <Card>
            <CardHeader>
              <CardTitle>Exportar landbot-data.json</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">
                Este archivo contiene la configuración de Landbot para todos los planes activos.
                Se utiliza para la integración con Landbot.
              </p>
              
              <Button 
                onClick={handleExportLandbotJson}
                disabled={!dataReady}
                className="flex items-center"
              >
                <Download className="mr-2 h-4 w-4" />
                Exportar landbot-data.json
              </Button>
              
              {!dataReady && (
                <p className="text-xs text-amber-600 mt-2">
                  Primero debes obtener los datos actualizados usando el botón "Obtener datos actualizados"
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}